import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import {
  emailTemplate,
  emailTemplateContentEnd,
  emailTemplateContentSection,
  emailTemplateContentStart,
  IEmailTemplate,
  IEmailTemplateInfo,
} from '@curbnturf/entities';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const API_URL = BASE_API_URL + 'email-template';

/** Wraps editor content in the standard CurbNTurf email layout (between the content markers). */
export function wrapContentInLayout(content: string): string {
  const [before, after] = emailTemplate.split(emailTemplateContentSection);
  return before + emailTemplateContentStart + content + emailTemplateContentEnd + after;
}

/**
 * The inverse, for editing: a stored template that contains the layout's
 * content markers is "wrapped" — only the content between them is editable.
 */
export function splitTemplateHtml(html: string): { content: string; wrapped: boolean } {
  const start = html.indexOf(emailTemplateContentStart);
  const end = html.indexOf(emailTemplateContentEnd);
  if (start === -1 || end === -1 || end < start) {
    return { content: html, wrapped: false };
  }
  return { content: html.slice(start + emailTemplateContentStart.length, end), wrapped: true };
}

/** SES email templates (server AdminAuthGuard). Moved from the app's services/email-templates.service.ts. */
@Injectable({ providedIn: 'root' })
export class AdminEmailTemplateApiService {
  private readonly http = inject(HttpClient);

  list(): Observable<IEmailTemplateInfo[]> {
    return this.http
      .get<{ name: string; created: string }[]>(API_URL)
      .pipe(map((templates) => (templates ?? []).map((t) => ({ name: t.name, created: new Date(t.created) }))));
  }

  fetch(name: string): Observable<IEmailTemplate> {
    return this.http.get<IEmailTemplate>(`${API_URL}/${encodeURIComponent(name)}`);
  }

  create(template: IEmailTemplate): Observable<IEmailTemplate> {
    return this.http.post<IEmailTemplate>(API_URL, template);
  }

  update(template: IEmailTemplate): Observable<IEmailTemplate> {
    return this.http.put<IEmailTemplate>(API_URL, template);
  }

  remove(name: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${API_URL}/${encodeURIComponent(name)}`);
  }
}
